import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, SubmitHandler } from 'react-hook-form';
import { Box, Container, Snackbar } from '@mui/material';
import { createOrUpdateApplicant } from 'apis';
import { useAuth } from 'app/ProviderAuth';
import { Button } from 'components';
import { putFileToStorage, getFileFromURL } from 'helper/storage';
import { ApplicantForm, ApplicantFormFieldValues } from './components/ApplicantForm';
import { Applicant, Referrer } from 'types';
interface EditApplicantProfilePageProps{
  ReferrerData: Referrer|undefined;
  ApplicantData: Applicant|undefined;
  setApplicant: React.Dispatch<React.SetStateAction<Applicant | undefined>>;
}

export const EditApplicantProfilePage: React.FC<EditApplicantProfilePageProps> =
({ReferrerData:referrer,  ApplicantData: applicant, setApplicant}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [userDocID, setUserDocID] = useState(user?.uid ?? '');
  const [snackBarOpen, setSnackBarOpen] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, errors },
    setValue,
    reset,
  } = useForm<ApplicantFormFieldValues>({
    defaultValues: {
      firstName: '',
      lastName: '',
      preferredName: '',
      countryOfResidence: '',
      occupation: '',
      schoolOrCompany: '',
      linkedIn: '',
      showLinkedIn: 'true',
      profilePicture: undefined,
      website: '',
      biography: '',
      email: '',
      phone: '',
      address: '',
      needSponsorship: '',
    },
    mode: 'onBlur',
  });

  useEffect(() => {
    if (user && (referrer || applicant)) {
      if (applicant?.profilePicture) {
        const fetchProfilePicture = async () => {
          const file = await getFileFromURL(applicant.profilePicture!);
          file && setValue('profilePicture', file);
        };
        fetchProfilePicture();
      }
      reset({
        firstName: applicant?.firstName || referrer?.name.split(' ')[0] || '',
        lastName: applicant?.lastName || referrer?.name.split(' ')[1] || '',
        preferredName: applicant?.preferredName || referrer?.preferredName || '',
        countryOfResidence: applicant?.countryOfResidence || '',
        occupation: applicant?.occupation || '',
        schoolOrCompany: applicant?.schoolOrCompany || '',
        linkedIn: applicant?.linkedIn || referrer?.linkedIn || '',
        showLinkedIn: applicant?.showLinkedIn || referrer?.showLinkedIn || 'true',
        website: applicant?.website || '',
        biography: applicant?.biography || referrer?.selfIntroduction || '',
        email: applicant?.email || user.email || '',
        phone: applicant?.phone || referrer?.phone || '',
        address: applicant?.address || '',
        needSponsorship: applicant?.needSponsorship || '',
      });
      setUserDocID(user.uid ?? '');
    }
  }, [referrer, applicant, reset, user, setValue]);

  const onSubmit: SubmitHandler<ApplicantFormFieldValues> = async (data) => {
    let profilePicturePath = '';
    if (data.profilePicture) {
      const filePath = `resume/${user!.uid}/${data.profilePicture.name}`;
      try {
        const downloadUrl = await putFileToStorage(data.profilePicture, filePath);
        profilePicturePath = downloadUrl;
      } catch (e) {
        console.error('fail uploading resume');
        return;
      }
    }
    const applicantData: Applicant = {
      ...data,
      profilePicture: profilePicturePath,
    };
    setApplicant(applicantData);
    await createOrUpdateApplicant(userDocID, applicantData);
    setSnackBarOpen(true);
  };
  const toProfilePage = () => {
    navigate(`/profile/${userDocID}`);
  };
  return (
    <Box my={5}>
      <Snackbar
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      autoHideDuration={5000}
      onClose={() => setSnackBarOpen(false)}
      open={snackBarOpen}
      message="成功更新個人資料!"
      action={
        <Button
          color='secondary'
          onClick={toProfilePage}
          sx= {{
            height: '40px',
            backgroundColor: 'transparent',
            color: '#47DE78',
            fontSize: '16px',
          }}
        >
          檢視個人資料
        </Button>
      }
    />
    <Container maxWidth="md" sx={{ my: 4 }}>
      <ApplicantForm
        control={control}
        errors={errors}
        isSubmitting={isSubmitting}
        submitted={snackBarOpen}
        handleSubmit={handleSubmit(onSubmit)}
      />
    </Container>
    </Box>
  );
};
